/**
 * TemplatesPage - Admin Template Management
 *
 * Displays and manages category templates with hierarchical structure.
 * Includes create, edit, delete, and broadcast functionality.
 *
 * @validates Requirements 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 7.3
 */

import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminTemplateStore } from '../../store/adminTemplateStore';
import {
  CategoryTemplateTreeNode,
  CreateCategoryTemplateRequest,
  UpdateCategoryTemplateRequest,
  BroadcastResult,
  CategoryBaseType,
} from '@budgira/shared';
import EmojiPicker from '../../components/EmojiPicker';

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className || 'w-4 h-4'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className || 'w-4 h-4'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const BroadcastIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
  </svg>
);

const FolderIcon = () => (
  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

// Helper to flatten template tree for parent selection
function flattenTemplates(
  templates: CategoryTemplateTreeNode[],
  maxLevel: number = 2
): CategoryTemplateTreeNode[] {
  const result: CategoryTemplateTreeNode[] = [];
  const flatten = (nodes: CategoryTemplateTreeNode[]) => {
    for (const node of nodes) {
      if (node.hierarchyLevel < maxLevel) {
        result.push(node);
      }
      if (node.children && node.children.length > 0) {
        flatten(node.children);
      }
    }
  };
  flatten(templates);
  return result;
}

// ===========================================
// DeleteConfirmDialog Component
// ===========================================
interface DeleteConfirmDialogProps {
  isOpen: boolean;
  template: CategoryTemplateTreeNode | null;
  onClose: () => void;
  onConfirm: (recursive: boolean) => Promise<void>;
}

const DeleteConfirmDialog = ({ isOpen, template, onClose, onConfirm }: DeleteConfirmDialogProps) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [recursive, setRecursive] = useState(false);

  const hasChildren = template?.children && template.children.length > 0;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onConfirm(recursive);
      onClose();
    } finally {
      setIsDeleting(false);
      setRecursive(false);
    }
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('admin.templates.delete', 'Supprimer le template')}
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mb-4">
          {t(
            'admin.templates.deleteConfirm',
            'Êtes-vous sûr de vouloir supprimer le template "{{name}}" ?',
            { name: template.name }
          )}
        </p>

        {hasChildren && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={recursive}
                onChange={(e) => setRecursive(e.target.checked)}
                className="w-4 h-4 text-violet-600 rounded focus:ring-violet-500"
              />
              <span className="text-amber-800 dark:text-amber-300">
                {t('admin.templates.deleteRecursive', 'Supprimer également les sous-catégories')}
              </span>
            </label>
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
              {t(
                'admin.templates.deleteRecursiveWarning',
                'Ce template contient {{count}} sous-catégorie(s). Cochez cette option pour les supprimer aussi.',
                { count: template.children?.length || 0 }
              )}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting || (hasChildren && !recursive)}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isDeleting ? t('common.loading') : t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// BroadcastDialog Component
// ===========================================
interface BroadcastDialogProps {
  isOpen: boolean;
  template: CategoryTemplateTreeNode | null;
  onClose: () => void;
  onConfirm: () => Promise<BroadcastResult | null>;
  estimateCount: number;
}

const BroadcastDialog = ({
  isOpen,
  template,
  onClose,
  onConfirm,
  estimateCount,
}: BroadcastDialogProps) => {
  const { t } = useTranslation();
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [result, setResult] = useState<BroadcastResult | null>(null);

  const handleBroadcast = async () => {
    setIsBroadcasting(true);
    try {
      const broadcastResult = await onConfirm();
      setResult(broadcastResult);
    } finally {
      setIsBroadcasting(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('admin.templates.broadcast', 'Diffuser aux utilisateurs')}
        </h2>

        {!result ? (
          <>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {t(
                'admin.templates.broadcastConfirm',
                'Diffuser le template "{{name}}" à {{count}} utilisateur(s) ?',
                { name: template.name, count: estimateCount }
              )}
            </p>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {t(
                  'admin.templates.broadcastInfo',
                  'Cette action créera une copie de ce template (et ses sous-catégories) pour chaque utilisateur qui ne l\'a pas encore.'
                )}
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleBroadcast}
                disabled={isBroadcasting || estimateCount === 0}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isBroadcasting ? t('common.loading') : t('common.confirm')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg mb-4">
              <p className="text-emerald-800 dark:text-emerald-300 font-medium mb-2">
                {t('admin.templates.broadcastSuccess', 'Diffusion terminée !')}
              </p>
              <ul className="text-sm text-emerald-700 dark:text-emerald-400 space-y-1">
                <li>
                  {t('admin.templates.broadcastCreated', 'Catégories créées : {{count}}', {
                    count: result.createdCount,
                  })}
                </li>
                <li>
                  {t('admin.templates.broadcastSkipped', 'Utilisateurs ignorés : {{count}}', {
                    count: result.skippedCount,
                  })}
                </li>
              </ul>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
              >
                {t('common.close', 'Fermer')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


// ===========================================
// CategoryTemplateForm Component
// ===========================================
interface CategoryTemplateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateCategoryTemplateRequest | UpdateCategoryTemplateRequest) => Promise<void>;
  editTemplate?: CategoryTemplateTreeNode | null;
  parentTemplate?: CategoryTemplateTreeNode | null;
  allTemplates: CategoryTemplateTreeNode[];
}

const CategoryTemplateForm = ({
  isOpen,
  onClose,
  onSave,
  editTemplate,
  parentTemplate,
  allTemplates,
}: CategoryTemplateFormProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [baseType, setBaseType] = useState<CategoryBaseType>('debit');
  const [selectedParentId, setSelectedParentId] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  // Determine if we're creating a root template or a child template
  const isRootTemplate = !parentTemplate && !editTemplate?.parentId && !selectedParentId;
  const inheritedType =
    parentTemplate?.baseType ||
    allTemplates.find((t) => t.id === selectedParentId)?.baseType ||
    editTemplate?.baseType;

  // Get available parents (max level 2)
  const availableParents = flattenTemplates(allTemplates, 3);

  useEffect(() => {
    if (editTemplate) {
      setName(editTemplate.name);
      setLogoUrl(editTemplate.logoUrl || '');
      setBaseType(editTemplate.baseType);
      setSelectedParentId(editTemplate.parentId);
    } else if (parentTemplate) {
      setName('');
      setLogoUrl('');
      setBaseType(parentTemplate.baseType);
      setSelectedParentId(parentTemplate.id);
    } else {
      setName('');
      setLogoUrl('');
      setBaseType('debit');
      setSelectedParentId(undefined);
    }
  }, [editTemplate, parentTemplate, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      if (editTemplate) {
        // Update - only name and logoUrl
        await onSave({
          name: name.trim(),
          logoUrl: logoUrl.trim() || undefined,
        } as UpdateCategoryTemplateRequest);
      } else {
        // Create
        await onSave({
          name: name.trim(),
          logoUrl: logoUrl.trim() || undefined,
          baseType: isRootTemplate ? baseType : undefined,
          parentId: selectedParentId || parentTemplate?.id,
        } as CreateCategoryTemplateRequest);
      }
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {editTemplate
            ? t('admin.templates.edit', 'Modifier le template')
            : parentTemplate
              ? t('admin.templates.createChild', 'Nouveau sous-template')
              : t('admin.templates.create', 'Créer un template')}
        </h2>

        {parentTemplate && (
          <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('admin.templates.parentLabel', 'Template parent :')}
            </p>
            <p className="font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <span>{parentTemplate.logoUrl}</span>
              {parentTemplate.name}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('admin.templates.form.name', 'Nom')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              placeholder={t('admin.templates.form.namePlaceholder', 'Ex: Alimentation, Transport...')}
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('admin.templates.form.icon', 'Icône')}
            </label>
            <div className="relative">
              <button
                ref={emojiButtonRef}
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-left flex items-center gap-2"
              >
                {logoUrl ? (
                  <>
                    <span className="text-2xl">{logoUrl}</span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('admin.templates.form.changeIcon', "Changer l'icône")}
                    </span>
                  </>
                ) : (
                  <span className="text-slate-400 dark:text-slate-500">
                    {t('admin.templates.form.selectIcon', 'Choisir une icône...')}
                  </span>
                )}
              </button>

              {showEmojiPicker && (
                <div className="absolute top-full left-0 mt-2 z-50">
                  <EmojiPicker
                    value={logoUrl}
                    onChange={(emoji) => setLogoUrl(emoji)}
                    onClose={() => setShowEmojiPicker(false)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Parent selection for new templates (not editing, not already with parent) */}
          {!editTemplate && !parentTemplate && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('admin.templates.form.parent', 'Template parent (optionnel)')}
              </label>
              <select
                value={selectedParentId || ''}
                onChange={(e) => setSelectedParentId(e.target.value || undefined)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              >
                <option value="">
                  {t('admin.templates.form.noParent', 'Aucun (catégorie racine)')}
                </option>
                {availableParents.map((parent) => (
                  <option key={parent.id} value={parent.id}>
                    {'—'.repeat(parent.hierarchyLevel - 1)} {parent.logoUrl} {parent.name}
                  </option>
                ))}
              </select>
            </div>
          )}


          {/* Base type selection for root templates only (new creation, no parent) */}
          {!editTemplate && isRootTemplate && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('admin.templates.form.type', 'Type')}
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="baseType"
                    value="debit"
                    checked={baseType === 'debit'}
                    onChange={() => setBaseType('debit')}
                    className="mr-2 text-violet-600 focus:ring-violet-500"
                  />
                  <span className="text-slate-700 dark:text-slate-300">
                    {t('admin.templates.form.debit', 'Débit (dépense)')}
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="baseType"
                    value="credit"
                    checked={baseType === 'credit'}
                    onChange={() => setBaseType('credit')}
                    className="mr-2 text-violet-600 focus:ring-violet-500"
                  />
                  <span className="text-slate-700 dark:text-slate-300">
                    {t('admin.templates.form.credit', 'Crédit (recette)')}
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Show inherited type for child templates or editing */}
          {(!isRootTemplate || editTemplate) && inheritedType && (
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('admin.templates.form.inheritedType', 'Type hérité :')}
                <span
                  className={`ml-2 font-medium ${inheritedType === 'credit' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}
                >
                  {inheritedType === 'credit' ? 'Crédit' : 'Débit'}
                </span>
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ===========================================
// CategoryTemplateList Component
// ===========================================
interface TemplateNodeProps {
  node: CategoryTemplateTreeNode;
  level: number;
  onEdit: (template: CategoryTemplateTreeNode) => void;
  onDelete: (template: CategoryTemplateTreeNode) => void;
  onAddChild: (parent: CategoryTemplateTreeNode) => void;
  onBroadcast: (template: CategoryTemplateTreeNode) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
}

const TemplateNode = ({
  node,
  level,
  onEdit,
  onDelete,
  onAddChild,
  onBroadcast,
  expandedIds,
  onToggleExpand,
}: TemplateNodeProps) => {
  const isExpanded = expandedIds.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const canAddChild = node.hierarchyLevel < 3;
  const indent = level * 24;

  return (
    <div>
      <div
        className="group flex flex-wrap md:flex-nowrap items-center py-2 px-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors gap-2"
        style={{ paddingLeft: `${indent + 12}px` }}
      >
        {/* Expand/collapse button */}
        <button
          onClick={() => onToggleExpand(node.id)}
          className={`p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors ${hasChildren ? 'visible' : 'invisible'}`}
        >
          {isExpanded ? (
            <ChevronDownIcon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          )}
        </button>

        {/* Logo/Icon */}
        <span className="text-lg flex-shrink-0">{node.logoUrl || <FolderIcon />}</span>

        {/* Name */}
        <span className="flex-1 font-medium text-slate-700 dark:text-slate-300 min-w-0 truncate">{node.name}</span>

        {/* Type badge - hidden on mobile */}
        <span
          className={`hidden md:inline-flex text-xs px-2 py-0.5 rounded-full ${
            node.baseType === 'credit'
              ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
              : 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-400'
          }`}
        >
          {node.baseType === 'credit' ? 'Crédit' : 'Débit'}
        </span>

        {/* Level badge - hidden on mobile */}
        <span className="hidden md:inline-flex text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-300">
          Niv. {node.hierarchyLevel}
        </span>

        {/* Actions - always visible on mobile, hover on desktop */}
        <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
          {canAddChild && (
            <button
              onClick={() => onAddChild(node)}
              className="p-1.5 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/40 text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              title="Ajouter un sous-template"
            >
              <PlusIcon />
            </button>
          )}
          <button
            onClick={() => onBroadcast(node)}
            className="p-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            title="Diffuser"
          >
            <BroadcastIcon />
          </button>
          <button
            onClick={() => onEdit(node)}
            className="p-1.5 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/40 text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
            title="Modifier"
          >
            <PencilIcon />
          </button>
          <button
            onClick={() => onDelete(node)}
            className="p-1.5 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/40 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
            title="Supprimer"
          >
            <TrashIcon />
          </button>
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children!.map((child) => (
            <TemplateNode
              key={child.id}
              node={child}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              onBroadcast={onBroadcast}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};


interface CategoryTemplateListProps {
  templates: CategoryTemplateTreeNode[];
  onEdit: (template: CategoryTemplateTreeNode) => void;
  onDelete: (template: CategoryTemplateTreeNode) => void;
  onAddChild: (parent: CategoryTemplateTreeNode) => void;
  onBroadcast: (template: CategoryTemplateTreeNode) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
}

const CategoryTemplateList = ({
  templates,
  onEdit,
  onDelete,
  onAddChild,
  onBroadcast,
  expandedIds,
  onToggleExpand,
}: CategoryTemplateListProps) => {
  const { t } = useTranslation();

  // Separate by type
  const debitTemplates = templates.filter((t) => t.baseType === 'debit');
  const creditTemplates = templates.filter((t) => t.baseType === 'credit');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Debit templates */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-rose-50 dark:bg-rose-900/30 rounded-t-xl">
          <h2 className="font-semibold text-rose-700 dark:text-rose-400 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-rose-500" />
            {t('admin.templates.debitTitle', 'Dépenses (Débit)')}
            <span className="text-sm font-normal text-rose-500 dark:text-rose-400">({debitTemplates.length})</span>
          </h2>
        </div>
        <div className="py-2">
          {debitTemplates.length === 0 ? (
            <p className="text-slate-400 dark:text-slate-500 text-center py-8">
              {t('admin.templates.noDebit', 'Aucun template de dépense')}
            </p>
          ) : (
            debitTemplates.map((template) => (
              <TemplateNode
                key={template.id}
                node={template}
                level={0}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddChild={onAddChild}
                onBroadcast={onBroadcast}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
              />
            ))
          )}
        </div>
      </div>

      {/* Credit templates */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-emerald-50 dark:bg-emerald-900/30 rounded-t-xl">
          <h2 className="font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-500" />
            {t('admin.templates.creditTitle', 'Revenus (Crédit)')}
            <span className="text-sm font-normal text-emerald-500 dark:text-emerald-400">({creditTemplates.length})</span>
          </h2>
        </div>
        <div className="py-2">
          {creditTemplates.length === 0 ? (
            <p className="text-slate-400 dark:text-slate-500 text-center py-8">
              {t('admin.templates.noCredit', 'Aucun template de revenu')}
            </p>
          ) : (
            creditTemplates.map((template) => (
              <TemplateNode
                key={template.id}
                node={template}
                level={0}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddChild={onAddChild}
                onBroadcast={onBroadcast}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};


// ===========================================
// CategoryTemplateManager (Main Component)
// ===========================================
export function TemplatesPage() {
  const { t } = useTranslation();
  const {
    templates,
    isLoading,
    error,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    estimateBroadcast,
    broadcastTemplate,
    clearError,
  } = useAdminTemplateStore();

  const [showFormModal, setShowFormModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState<CategoryTemplateTreeNode | null>(null);
  const [parentTemplate, setParentTemplate] = useState<CategoryTemplateTreeNode | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CategoryTemplateTreeNode | null>(null);
  const [broadcastTarget, setBroadcastTarget] = useState<CategoryTemplateTreeNode | null>(null);
  const [broadcastEstimate, setBroadcastEstimate] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Helper to collect all template IDs recursively
  const getAllTemplateIds = (nodes: CategoryTemplateTreeNode[]): string[] => {
    const ids: string[] = [];
    const collect = (node: CategoryTemplateTreeNode) => {
      ids.push(node.id);
      node.children?.forEach(collect);
    };
    nodes.forEach(collect);
    return ids;
  };

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Initialize expanded state when templates load - expand all by default
  useEffect(() => {
    if (templates.length > 0 && expandedIds.size === 0) {
      const allIds = getAllTemplateIds(templates);
      setExpandedIds(new Set(allIds));
    }
  }, [templates]);

  const handleToggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    const allIds = getAllTemplateIds(templates);
    setExpandedIds(new Set(allIds));
  };

  const handleCollapseAll = () => {
    setExpandedIds(new Set());
  };

  const handleAddRoot = () => {
    setEditTemplate(null);
    setParentTemplate(null);
    setShowFormModal(true);
  };

  const handleAddChild = (parent: CategoryTemplateTreeNode) => {
    setEditTemplate(null);
    setParentTemplate(parent);
    setShowFormModal(true);
  };

  const handleEdit = (template: CategoryTemplateTreeNode) => {
    setEditTemplate(template);
    setParentTemplate(null);
    setShowFormModal(true);
  };

  const handleDelete = (template: CategoryTemplateTreeNode) => {
    setDeleteTarget(template);
  };

  const handleBroadcast = async (template: CategoryTemplateTreeNode) => {
    const count = await estimateBroadcast(template.id);
    setBroadcastEstimate(count);
    setBroadcastTarget(template);
  };

  const handleSave = async (
    data: CreateCategoryTemplateRequest | UpdateCategoryTemplateRequest
  ) => {
    if (editTemplate) {
      await updateTemplate(editTemplate.id, data as UpdateCategoryTemplateRequest);
    } else {
      await createTemplate(data as CreateCategoryTemplateRequest);
    }
  };

  const handleConfirmDelete = async (recursive: boolean) => {
    if (deleteTarget) {
      await deleteTemplate(deleteTarget.id, recursive);
    }
  };

  const handleConfirmBroadcast = async (): Promise<BroadcastResult | null> => {
    if (broadcastTarget) {
      return await broadcastTemplate(broadcastTarget.id);
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('admin.templates.title', 'Templates de catégories')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('admin.templates.subtitle', 'Gérez les templates de catégories par défaut')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Expand/Collapse toggle */}
          {templates.length > 0 && (
            <button
              onClick={expandedIds.size > 0 ? handleCollapseAll : handleExpandAll}
              className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title={expandedIds.size > 0 ? t('admin.templates.collapseAll', 'Tout masquer') : t('admin.templates.expandAll', 'Tout déployer')}
            >
              {expandedIds.size > 0 ? (
                <ChevronRightIcon className="w-5 h-5" />
              ) : (
                <ChevronDownIcon className="w-5 h-5" />
              )}
            </button>
          )}
          <button
            onClick={handleAddRoot}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            <PlusIcon />
            <span>{t('admin.templates.create', 'Nouveau template')}</span>
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-lg p-4 flex items-center justify-between">
          <p className="text-rose-700 dark:text-rose-400">{error}</p>
          <button onClick={clearError} className="text-rose-500 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300">
            ✕
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && templates.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
        </div>
      )}


      {/* Empty state */}
      {!isLoading && templates.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
            <FolderIcon />
          </div>
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t('admin.templates.empty.title', 'Aucun template')}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            {t(
              'admin.templates.empty.message',
              'Créez votre premier template pour définir les catégories par défaut.'
            )}
          </p>
          <button
            onClick={handleAddRoot}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            <PlusIcon />
            <span>{t('admin.templates.empty.create', 'Créer un template')}</span>
          </button>
        </div>
      ) : (
        <CategoryTemplateList
          templates={templates}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAddChild={handleAddChild}
          onBroadcast={handleBroadcast}
          expandedIds={expandedIds}
          onToggleExpand={handleToggleExpand}
        />
      )}

      {/* Modals */}
      <CategoryTemplateForm
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setEditTemplate(null);
          setParentTemplate(null);
        }}
        onSave={handleSave}
        editTemplate={editTemplate}
        parentTemplate={parentTemplate}
        allTemplates={templates}
      />

      <DeleteConfirmDialog
        isOpen={!!deleteTarget}
        template={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />

      <BroadcastDialog
        isOpen={!!broadcastTarget}
        template={broadcastTarget}
        onClose={() => {
          setBroadcastTarget(null);
          setBroadcastEstimate(0);
        }}
        onConfirm={handleConfirmBroadcast}
        estimateCount={broadcastEstimate}
      />
    </div>
  );
}


export default TemplatesPage;
